import React from "react";
import { Dialog, Box, Typography, TextField, Button, CircularProgress, Toolbar, IconButton, List } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ImageCropField } from "../../components/ImageCropField";
import { ImageCropState } from "../../hooks/useImageCrop";

type Props = {
  open: boolean;
  isEditApi: boolean;
  companyData: any;
  errors: any;
  loader: boolean;
  imageCropEn: ImageCropState;
  imageCropGer: ImageCropState;
  onClose: () => void;
  onChange: (e: any) => void;
  onSubmit: () => void;
  onRemoveCompanyLogo?: () => void;
  onRemoveCompanyLogoGer?: () => void;
};

export const CompanyFormDialog = ({
  open, isEditApi, companyData, errors, loader,
  imageCropEn, imageCropGer, onClose, onChange, onSubmit,
  onRemoveCompanyLogo, onRemoveCompanyLogoGer,
}: Props) => {
  if (!open) return null;

  return (
    <Dialog fullScreen open={open}>
      <Toolbar>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" sx={{ ml: "auto" }}>
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <List>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
            </Box>
          </Box>

          <DualRow label="Name der Firma">
            <LangField id="companyName_ger" name="companyName_ger" value={companyData?.companyName_ger} error={errors["companyName_ger"]} onChange={onChange} />
            <LangField id="companyName" name="companyName" value={companyData?.companyName} error={errors["companyName"]} onChange={onChange} />
          </DualRow>

          <SharedRow label="Email">
            <TextField size="small" fullWidth variant="outlined" id="email" name="email" value={companyData?.email || ""} onChange={onChange} />
            <Typography variant="caption" color="error">{errors["email"]}</Typography>
          </SharedRow>

          <SharedRow label="Telefon-Nr">
            <TextField size="small" fullWidth variant="outlined" type="tel" id="telephoneNo" name="telephoneNo" value={companyData?.telephoneNo || ""} onChange={onChange} />
            <Typography variant="caption" color="error">{errors["telephoneNo"]}</Typography>
          </SharedRow>

          <SharedRow label="Webseite">
            <TextField size="small" fullWidth variant="outlined" id="website" name="website" value={companyData?.website || ""} onChange={onChange} />
            <Typography variant="caption" color="error">{errors["website"]}</Typography>
          </SharedRow>

          <DualRow label="Firmen Beschreibung">
            <LangField id="companyDetails_ger" name="companyDetails_ger" value={companyData?.companyDetails_ger} error={errors["companyDetails_ger"]} onChange={onChange} multiline />
            <LangField id="companyDetails" name="companyDetails" value={companyData?.companyDetails} error={errors["companyDetails"]} onChange={onChange} multiline />
          </DualRow>

          <DualRow label="Adresse">
            <LangField id="address_ger" name="address_ger" value={companyData?.address_ger} error={errors["address_ger"]} onChange={onChange} multiline />
            <LangField id="address" name="address" value={companyData?.address} error={errors["address"]} onChange={onChange} multiline />
          </DualRow>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Firmenlogo</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField inputId="companyLogo_ger" imageCrop={imageCropGer} existingImage={companyData?.companyLogo_ger} error={errors["companyLogo_ger"]} onRemoveExistingImage={onRemoveCompanyLogoGer} />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField inputId="companyLogo" imageCrop={imageCropEn} existingImage={companyData?.companyLogo} error={errors["companyLogo"]} onRemoveExistingImage={onRemoveCompanyLogo} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loader ? (
            <Button variant="contained" disabled>
              {isEditApi ? "Speichern" : "Hinzufügen"}
              <CircularProgress size={20} sx={{ ml: 3, color: "white" }} />
            </Button>
          ) : (
            <Button variant="contained" onClick={onSubmit}>
              {isEditApi ? "Speichern" : "Hinzufügen"}
            </Button>
          )}
        </Box>
      </List>
    </Dialog>
  );
};

/* Helper components to reduce repetition */

const DualRow = ({ label, children }: { label: string; children: [React.ReactNode, React.ReactNode] }) => (
  <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
    <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>{label}</Typography>
    <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>{children[0]}</Box>
    <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>{children[1]}</Box>
  </Box>
);

const SharedRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
    <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>{label}</Typography>
    <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>{children}</Box>
  </Box>
);

type LangFieldProps = {
  id: string;
  name: string;
  value: string;
  error?: string;
  onChange: (e: any) => void;
  multiline?: boolean;
};

const LangField = ({ id, name, value, error, onChange, multiline }: LangFieldProps) => (
  <>
    <TextField
      size="small"
      fullWidth
      variant="outlined"
      multiline={multiline}
      id={id}
      name={name}
      value={value || ""}
      onChange={onChange}
    />
    <Typography variant="caption" color="error">{error}</Typography>
  </>
);
